import { useState } from 'react';
import Button from '@/components/base/Button';
import { api } from '@/utils/api';

interface Insight {
  flags: { severity: 'warning' | 'error'; creator: string; issue: string; action: string }[];
  summary: string;
  source: 'ai' | 'basic';
}

/** The live Payment tab's AI insight (GET campaigns/{id}/payments-insight): flagged payments and a summary. */
export default function PaymentsInsight({ campaignId }: { campaignId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [insight, setInsight] = useState<Insight | null>(null);

  const analyse = async () => {
    setBusy(true);
    setError('');
    try {
      setInsight(await api<Insight>(`/campaigns/${campaignId}/payments-insight`));
    } catch (analyseError) {
      setInsight(null);
      setError(analyseError instanceof Error ? analyseError.message : 'Could not load insight. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-accent-200 bg-accent-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
            <i className="ri-sparkling-2-line" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-accent-900">Payment insight</h2>
            {insight ? (
              <p className="mt-1 text-sm leading-relaxed text-accent-800">{insight.summary}</p>
            ) : (
              <p className="mt-0.5 text-xs text-accent-800">
                Checks for approved content not yet payable, stalled payments and payable creators left waiting.
              </p>
            )}
          </div>
        </div>
        <Button size="sm" variant="outline" icon={busy ? 'ri-loader-4-line' : 'ri-line-chart-line'} disabled={busy} onClick={() => void analyse()}>
          {busy ? 'Analysing…' : insight ? 'Analyse again' : 'Analyse payments'}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="mt-3 flex items-start gap-1.5 text-xs text-primary-700">
          <i className="ri-error-warning-line mt-0.5" />
          {error}
        </p>
      ) : null}
      {insight && insight.flags.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {insight.flags.map((flag, index) => (
            <li key={`${flag.creator}-${index}`} className="flex items-start gap-2 rounded-md bg-background-50 px-3 py-2 text-xs">
              <i className={`mt-0.5 ${flag.severity === 'error' ? 'ri-error-warning-fill text-primary-600' : 'ri-alert-line text-secondary-700'}`} />
              <div>
                <p className="text-foreground-900">
                  <span className="font-medium">{flag.creator}</span> — {flag.issue}
                </p>
                <p className="text-foreground-500">{flag.action}</p>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {insight && insight.source !== 'ai' ? (
        <p className="mt-3 text-[11px] text-accent-800/70">Rule-based result: the AI summary wasn't available.</p>
      ) : null}
    </section>
  );
}
